import * as React from "react"
import { useQuery } from "react-query"
import { getTranslations } from "../api"
import { queryStaleTime } from "../constants"

export type LocalesKeys =
	| "readMore"
	| "showMore"
	| "showLess"
	| "viewMore"
	| "viewLess"
	| "open"
	| "close"
	| "next"
	| "prev"
	| "show"
	| "hide"
	| "commonMessagesPlural"
	| "commonMessagesSingular"
	| "localMessagesSingular"
	| "localMessagesPlural"
	| "googlePlay"
	| "appStore"
	| "searchCareInAppLabel"
	| "searchCareInAppSubmitLabel"
	| "unitFilterResultCount"
	| "filter"
	| "regions"
	| "typeOfCare"
	| "toPageTop"
	| "skipLinkLabel"
	| "playVideo"
	| "county"
	| "jobType"
	| "employmentType"
	| "requiredFieldMessage"
	| "careerFilterResultCount"
	| "careerSpontaniousFilterResultCount"
	| "forgotPasswordQuestion"
	| "abort"
	| "relatedDiagnoses"
	| "backToLogin"
	| "nextStep"
	| "selectRegion"
	| "selectUnit"
	| "all"
	| "goToMyPages"
	| "chooseLater"
	| "save"
	| "saved"
	| "openModalFor"
	| "moreOptions"
	| "manageAccount"
	| "remove"
	| "removeAccountConfirmationQuery"
	| "manageAccountFeedbackMessageSuccess"
	| "logout"
	| "login"
	| "article"
	| "newsResultCount"
	| "newsResultCountSingular"
	| "fileFormat"
	| "imageSize"
	| "licence"
	| "usageRights"
	| "photographer"
	| "published"
	| "stopPublish"
	| "download"
	| "reference"
	| "assetsResultCount"
	| "subjects"
	| "organisation"
	| "showAlls"
	| "showDetails"
	| "hideDetails"
	| "search"
	| "totalSearchResults"
	| "searchPlaceholder"
	| "menu"
	| "suggestion"
	| "reviewer"
	| "lastReviewDate"
	| "references"
	| "copyLink"
	| "news"
	| "loadUnits"
	| "loadArticles"
	| "loadNews"
	| "checkAll"
	| "useMyPosition"
	| "fieldOfCare"
	| "diagnoses"
	| "unitFilterResultCountMobile"
	| "clear"
	| "filter"
	| "toFilter"

export type TranslationsI = Record<LocalesKeys, string>

export interface TranslationsList {
	language: string
	translations: TranslationsI
}

export interface LocalesContextProviderProps {
	localeSlug: string
	children: React.ReactNode
}

export interface LocalesContextValue {
	translate?: Function
	translationKeys: Record<LocalesKeys, string>
}

export const LocalesContext = React.createContext<LocalesContextValue>({
	translate: null,
	translationKeys: null,
})

function translationsArrayToRecord(
	translationsList: TranslationsList[],
	localeSlug: string
): TranslationsI {
	return translationsList.find((t) => t.language === localeSlug)?.translations
}

export const LocalesContextProvider: React.FC<LocalesContextProviderProps> = (
	props
) => {
	const { data, isLoading } = useQuery<TranslationsList[]>(
		"translations",
		() => getTranslations(),
		{
			staleTime: queryStaleTime,
		}
	)

	const translations =
		data?.length > 0 ? translationsArrayToRecord(data, props.localeSlug) : null
	const keys = data?.length > 0 && translations ? Object.keys(translations) : []

	return (
		<LocalesContext.Provider
			value={{
				translationKeys: Object.fromEntries(keys.map((a) => [a, a])) as Record<
					LocalesKeys,
					string
				>,
				translate: (key: string) =>
					translations != null && translations[key] != null
						? translations[key]
						: "missing translation",
			}}
		>
			{!isLoading && props.children}
		</LocalesContext.Provider>
	)
}
